import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';
import { ExceptionsService } from '../../services/utelize/general/exceptions-service';

@Directive({ selector: '[appModalResizable]' })

export class ModalResizableDirective implements AfterViewInit, OnDestroy {

  @Input() public minHeight: number=360;
  @Input() public minWidth : number=480;
  @Input() public topMargin: number=24;

  private activeHandle : string='';
  private dialog       : HTMLElement | undefined;
  private handles      : HTMLElement[]=[];
  private removeMoveFn : (() => void) | undefined;
  private removeUpFn   : (() => void) | undefined;
  private startHeight  : number=0;
  private startLeft    : number=0;
  private startPointerX: number=0;
  private startPointerY: number=0;
  private startTop     : number=0;
  private startWidth   : number=0;

  constructor(
    private elementRef       : ElementRef<HTMLElement>,
    private exceptionsService: ExceptionsService,
    private renderer         : Renderer2
  ) { }

  public ngAfterViewInit(): void {
    try {
      setTimeout(() => this.initialise());
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.handles.forEach(handle => handle.remove());
      this.removeMoveFn?.();
      this.removeUpFn?.();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private addHandle(position: string): void {
    try {
      if (!this.dialog)
        return;

      const handle: HTMLElement=this.renderer.createElement('div');

      this.renderer.addClass(handle, 'modal-resize-handle'            );
      this.renderer.addClass(handle, 'modal-resize-handle-' + position);

      this.renderer.listen(handle, 'pointerdown', (pointerEvent: PointerEvent) => this.onPointerDown(pointerEvent, position));
      this.renderer.appendChild(this.dialog, handle);

      this.handles.push(handle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private clamp(value: number, min: number, max: number): number {
    try {
      return Math.min(Math.max(value, min), max);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return value;
    }
  }

  private initialise(): void {
    try {
      this.dialog=<HTMLElement>this.elementRef.nativeElement.closest('.modal-dialog');

      if (!this.dialog)
        return;

      const rect  : DOMRect=this.dialog.getBoundingClientRect();
      const height: number =this.clamp(rect.height, this.minHeight, window.innerHeight-(this.topMargin*2)   );
      const top   : number =this.clamp(rect.top   , this.topMargin, window.innerHeight-height-this.topMargin);

      this.renderer.addClass(this.dialog, 'modal-dialog-resizable');

      this.renderer.setStyle(this.dialog, 'position' , 'fixed'        );
      this.renderer.setStyle(this.dialog, 'margin'   , '0'            );
      this.renderer.setStyle(this.dialog, 'max-width', 'none'         );
      this.renderer.setStyle(this.dialog, 'width'    , rect.width+'px');
      this.renderer.setStyle(this.dialog, 'height'   , height    +'px');
      this.renderer.setStyle(this.dialog, 'left'     , rect.left +'px');
      this.renderer.setStyle(this.dialog, 'top'      , top       +'px');

      ['top', 'right', 'bottom', 'left', 'top-left', 'top-right', 'bottom-left', 'bottom-right']
        .forEach(position => this.addHandle(position));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private onPointerDown(pointerEvent: PointerEvent, position: string): void {
    try {
      if (!this.dialog)
        return;

      pointerEvent.preventDefault();
      pointerEvent.stopPropagation();

      const rect: DOMRect=this.dialog.getBoundingClientRect();

      this.activeHandle =position;
      this.startHeight  =rect.height;
      this.startLeft    =rect.left;
      this.startPointerX=pointerEvent.clientX;
      this.startPointerY=pointerEvent.clientY;
      this.startTop     =rect.top;
      this.startWidth   =rect.width;

      this.renderer.setStyle(document.body, 'user-select', 'none');

      this.removeMoveFn=this.renderer.listen('document', 'pointermove', (moveEvent: PointerEvent) => this.onPointerMove(moveEvent));
      this.removeUpFn  =this.renderer.listen('document', 'pointerup',   (                       ) => this.onPointerUp  (         ));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private onPointerMove(pointerEvent: PointerEvent): void {
    try {
      if (!this.dialog || !this.activeHandle)
        return;

      const deltaX   : number=pointerEvent.clientX-this.startPointerX;
      const deltaY   : number=pointerEvent.clientY-this.startPointerY;
      const maxWidth : number=window.innerWidth;
      const maxHeight: number=window.innerHeight;

      let height: number=this.startHeight;
      let left  : number=this.startLeft;
      let top   : number=this.startTop;
      let width : number=this.startWidth;

      if (this.activeHandle.includes('right'))
        width=this.clamp(this.startWidth+deltaX, this.minWidth, maxWidth-this.startLeft);

      if (this.activeHandle.includes('left')) {
        width=this.clamp(this.startWidth-deltaX, this.minWidth, this.startLeft+this.startWidth);
        left =this.startLeft+(this.startWidth-width);
      }

      if (this.activeHandle.includes('bottom'))
        height=this.clamp(this.startHeight+deltaY, this.minHeight, maxHeight-this.startTop);

      if (this.activeHandle.includes('top')) {
        height=this.clamp(this.startHeight-deltaY, this.minHeight, this.startTop+this.startHeight);
        top   =this.startTop + (this.startHeight-height);
      }

      this.renderer.setStyle(this.dialog, 'height', height+'px');
      this.renderer.setStyle(this.dialog, 'left',   left  +'px');
      this.renderer.setStyle(this.dialog, 'top',    top   +'px');
      this.renderer.setStyle(this.dialog, 'width',  width +'px');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private onPointerUp(): void {
    try {
      this.activeHandle='';

      this.renderer.removeStyle(document.body, 'user-select');
      this.removeMoveFn?.();
      this.removeUpFn?.();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    try {
      if (!this.dialog)
        return;

      const rect  : DOMRect=this.dialog.getBoundingClientRect();
      const width : number =this.clamp(rect.width , this.minWidth , window.innerWidth        );
      const height: number =this.clamp(rect.height, this.minHeight, window.innerHeight       );
      const left  : number =this.clamp(rect.left  , 0             , window.innerWidth -width );
      const top   : number =this.clamp(rect.top   , 0             , window.innerHeight-height);

      this.renderer.setStyle(this.dialog, 'height', height+'px');
      this.renderer.setStyle(this.dialog, 'left',   left  +'px');
      this.renderer.setStyle(this.dialog, 'top',    top   +'px');
      this.renderer.setStyle(this.dialog, 'width',  width +'px');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
